import { Flag, Loader2, Send } from "lucide-react";
import type { FC } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

import { goesBack } from "../reviewModel";
import type { SubmissionReview } from "../useSubmissionReview";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: SubmissionReview;
};

/** Send the flagged answers back: what goes, with which notes, and the email's opening line. */
export const RevisionDialog: FC<Props> = ({ open, onOpenChange, review }) => {
  const { flaggedRows, counts, clientFirstName } = review;
  const who = clientFirstName ?? "your client";
  const n = flaggedRows.length;
  const sending = review.busy === "revision";
  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            Send {n === 1 ? "1 answer" : `${n} answers`} back to {who}
          </DialogTitle>
          <DialogDescription>
            {goesBack(n)} with your notes. {clientFirstName ? "Her" : "The"}{" "}
            current link keeps working and opens straight on what you flagged.
            {counts.accepted
              ? ` The ${counts.accepted} accepted ${
                  counts.accepted === 1 ? "answer stays" : "answers stay"
                } accepted.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="flex max-h-48 flex-col gap-1.5 overflow-y-auto rounded-md border border-border p-2.5">
          {flaggedRows.map((r) => (
            <div key={r.key} className="flex items-start gap-2 text-xs">
              <Flag className="mt-0.5 size-3 shrink-0 text-warn" />
              <div className="flex min-w-0 flex-col">
                <span className="font-medium">
                  {r.context ? `${r.context} · ` : ""}
                  {r.label}
                </span>
                {r.note.trim() ? (
                  <span className="text-muted-foreground">
                    “{r.note.trim()}”
                  </span>
                ) : (
                  <span className="text-muted-foreground italic">
                    No note. {who} sees only that this answer needs another
                    look.
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="revision-note" className="text-xs">
            Message at the top of the email
          </Label>
          <Textarea
            id="revision-note"
            rows={4}
            value={review.revisionNote}
            onChange={(e) => review.setRevisionNote(e.target.value)}
            className="text-sm"
          />
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            Drafted from your notes. Edit freely.
            {review.revisionNote !== "" ? (
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={review.resetRevisionNote}
              >
                Redraft
              </button>
            ) : null}
          </span>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            disabled={sending}
            onClick={() => onOpenChange(false)}
          >
            Keep reviewing
          </Button>
          <Button
            variant="primary"
            disabled={sending || n === 0}
            onClick={async () => {
              if (await review.requestRevision()) onOpenChange(false);
            }}
          >
            {sending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Send className="size-3.5" />
            )}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
